import React, { useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AuthContext } from "../context/AuthContext";

import AdminLayout from "../layouts/AdminLayout";
import HeaderStats from "../components/Headers/HeaderStats";
import CardBarChart from "../components/Cards/CardBarChart";
import CardTopClients from "../components/Cards/CardTopClients";
import CardTopProducts from "../components/Cards/CardTopProducts";

import {
  getSalesSummary,
  getSalesOverTime,
  getTopClients,
  getTopProducts,
} from "../controllers/apiReports";

import { getClients } from "../controllers/api_clients";

export default function Dashboard({ collapsed }) {
  const { user } = useContext(AuthContext);

  const isAdmin = (user?.rol === "admin" || user?.rol === "auditor");
  const empresa_id = user?.empresa_id;
  const nombreUsuario = user?.nombre || "Usuario";

  const [draftStartDate, setDraftStartDate] = useState("");
  const [draftEndDate, setDraftEndDate] = useState("");

  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");

  const [selectedClientId, setSelectedClientId] = useState(
    isAdmin ? "" : empresa_id
  );

  const [topClientsLimit, setTopClientsLimit] = useState(5);
  const [topProductsLimit, setTopProductsLimit] = useState(5);

  // (2026-05-13) Req 2: selector de moneda del dashboard.
  // Valores soportados: 'VES' (Bs), 'extranjera' (toma la del cliente filtrado),
  // o codigo explicito (USD/EUR). 'extranjera' solo aplica con cliente fijo.
  const [currencyView, setCurrencyView] = useState("VES");

  const applyFilter = () => {
    setAppliedStartDate(draftStartDate);
    setAppliedEndDate(draftEndDate);
  };

  const clearFilter = () => {
    setDraftStartDate("");
    setDraftEndDate("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    if (isAdmin) setSelectedClientId("");
  };

  const clientsQuery = useQuery({
    queryKey: ["clients"],
    enabled: isAdmin,
    initialData: [],
    queryFn: async () => {
      const res = await getClients();
      return Array.isArray(res?.data) ? res.data : [];
    },
  });

  const effectiveClientId = isAdmin
    ? selectedClientId || undefined
    : empresa_id;

  // (2026-05-13) Req 2: si el admin saca el filtro de cliente y la moneda
  // seleccionada era 'extranjera', cambia a VES automaticamente para evitar
  // el 400 del backend (no se puede usar 'extranjera' en GLOBAL).
  useEffect(() => {
    if (isAdmin && !effectiveClientId && currencyView === 'extranjera') {
      setCurrencyView('VES');
    }
  }, [isAdmin, effectiveClientId, currencyView]);

  const params = {
    ...(effectiveClientId && { empresa_id: effectiveClientId }),
    ...(appliedStartDate && { start_date: appliedStartDate }),
    ...(appliedEndDate && { end_date: appliedEndDate }),
    // (2026-05-13) Req 2: propagar la moneda elegida a todos los endpoints
    // del dashboard (sales-summary / top-clients / top-products / sales-over-time).
    currency: currencyView,
  };

  const summaryQuery = useQuery({
    queryKey: ["sales-summary", params],
    enabled: isAdmin || !!empresa_id,
    queryFn: () => getSalesSummary(params),
    keepPreviousData: true,
  });

  const salesOverTimeQuery = useQuery({
    queryKey: ["sales-over-time", params],
    enabled: isAdmin || !!empresa_id,
    keepPreviousData: true,
    initialData: { currency: "", data: [] },
    queryFn: async () => {
      const res = await getSalesOverTime(params);
      return {
        currency: res?.currency || "",
        data: Array.isArray(res?.data) ? res.data : [],
      };
    },
  });

  const topClientsQuery = useQuery({
    queryKey: ["top-clients", params],
    enabled: isAdmin || !!empresa_id,
    initialData: [],
    queryFn: async () => {
      const res = await getTopClients(params);
      return Array.isArray(res) ? res : [];
    },
  });

  const topProductsQuery = useQuery({
    queryKey: ["top-products", params],
    enabled: isAdmin || !!empresa_id,
    initialData: [],
    queryFn: async () => {
      const res = await getTopProducts(params);
      return Array.isArray(res) ? res : [];
    },
  });

  return (
    <AdminLayout nombreUsuario={nombreUsuario}>
      
      {/* 🔵 HERO SIRIUS */}
      <div className="relative w-full rounded-3xl overflow-hidden shadow-sirius-lg">
        <div className="absolute inset-0 bg-sirius-night" />
        <div className="absolute inset-0 bg-sirius-mesh opacity-30" />
        <div className="pointer-events-none absolute -top-24 -right-16 w-80 h-80 rounded-full bg-spark-500/25 blur-[110px]" />
        <div className="pointer-events-none absolute -bottom-24 -left-10 w-72 h-72 rounded-full bg-sirius-600/25 blur-[110px]" />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)", backgroundSize: "44px 44px" }}
        />

        <div className="relative px-5 md:px-8 py-6">
          {/* Saludo dinámico */}
          <div className="mb-5">
            <h1 className="text-white font-display font-bold text-2xl md:text-3xl leading-tight">
              {(() => { const h = new Date().getHours(); return h < 12 ? "Buenos días" : h < 19 ? "Buenas tardes" : "Buenas noches"; })()},{" "}
              <span className="text-gradient-sirius">{nombreUsuario || "Usuario"}</span>
            </h1>
            <p className="text-white/55 text-sm mt-1">
              Resumen de tu actividad de facturación
            </p>
          </div>

          {/* FILTROS */}
          <div className="flex flex-wrap gap-2 items-center pb-4">
            <span className="text-xs text-white/50 font-semibold uppercase tracking-wider mr-1">
              Filtrar por:
            </span>

            {isAdmin && (
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="rounded-lg px-3 py-1.5 text-sm bg-white/10 border border-white/20 text-white backdrop-blur-md focus:outline-none focus:ring-2 focus:ring-spark-400/40 [&>option]:bg-[#1A1A1A] [&>option]:text-white"
              >
                <option value="">Todos los clientes</option>
                {clientsQuery.data.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.nombre_empresa}
                  </option>
                ))}
              </select>
            )}

            <input
              type="date"
              value={draftStartDate}
              onChange={(e) => setDraftStartDate(e.target.value)}
              className="rounded-lg px-3 py-1.5 text-sm bg-white/10 border border-white/20 text-white backdrop-blur-md [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-spark-400/40"
            />

            <input
              type="date"
              value={draftEndDate}
              onChange={(e) => setDraftEndDate(e.target.value)}
              className="rounded-lg px-3 py-1.5 text-sm bg-white/10 border border-white/20 text-white backdrop-blur-md [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-spark-400/40"
            />

            {/* (2026-05-13) Req 2: Selector de moneda del dashboard.
                - VES (Bs) — siempre disponible.
                - Para operador (cliente fijo): "Moneda extranjera (cliente)" toma
                  la moneda extranjera configurada del cliente.
                - Para admin/auditor (vista GLOBAL o filtro por cliente): expone
                  USD y EUR explicitos. El backend filtra las facturas cuya
                  moneda_extranjera coincide, evitando mezclar USD y EUR en la
                  misma suma. */}
            <select
              value={currencyView}
              onChange={(e) => setCurrencyView(e.target.value)}
              className="rounded-lg px-3 py-1.5 text-sm bg-white/10 border border-white/20 text-white backdrop-blur-md focus:outline-none focus:ring-2 focus:ring-spark-400/40 [&>option]:bg-[#1A1A1A] [&>option]:text-white"
              title="Moneda de visualización"
            >
              <option value="VES">VES (Bs.)</option>
              {/* 'extranjera' solo es semanticamente claro cuando se filtra por
                  un cliente especifico — usamos esa opcion para operadores o
                  para admin con cliente seleccionado. */}
              {(!isAdmin || effectiveClientId) && (
                <option value="extranjera">Moneda extranjera (cliente)</option>
              )}
              {/* Codigos explicitos disponibles para admin/auditor en cualquier
                  scope, y para operador si necesita drill por moneda. */}
              {isAdmin && (
                <>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </>
              )}
            </select>

            <button
              onClick={applyFilter}
              className="inline-flex items-center gap-1.5 bg-white text-sirius-700 hover:bg-sirius-50 px-4 py-1.5 rounded-lg text-sm font-display font-semibold transition-colors shadow-spark"
            >
              <i className="fas fa-filter text-xs" /> Aplicar
            </button>

            <button
              onClick={clearFilter}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              data-tooltip="Restablecer todos los filtros"
              data-tooltip-pos="bottom"
              aria-label="Limpiar filtros"
            >
              <i className="fa-solid fa-eraser" />
            </button>
          </div>

          {/* STATS */}
          <HeaderStats
            summary={summaryQuery.data}
            isLoading={summaryQuery.isFetching}
            collapsed={collapsed}
          />

          {/* (2026-05-16 Req#4) Nota aclaratoria de divisas: los totales en
              moneda extranjera (USD/EUR) son SOLO facturas emitidas en esa
              moneda; en Bs. se incluyen todas (montos convertidos). */}
          <div className="mt-3 flex items-start gap-2 text-xs text-white/60">
            <i className="fas fa-circle-info mt-0.5 text-white/50" />
            <p className="leading-relaxed">
              {currencyView === "USD" &&
                "Los totales en USD corresponden únicamente a la sumatoria de facturas emitidas en dólares."}
              {currencyView === "EUR" &&
                "Los totales en EUR corresponden únicamente a la sumatoria de facturas emitidas en euros."}
              {currencyView === "extranjera" &&
                "Los totales corresponden únicamente a la sumatoria de facturas emitidas en la moneda extranjera del cliente."}
              {currencyView === "VES" &&
                "Los totales en Bs. incluyen TODAS las facturas; los montos en moneda extranjera se convierten a Bs. según la tasa aplicada."}
            </p>
          </div>
        </div>
      </div>

      {/* ⚪ CONTENIDO NORMAL */}
      <div className="px-4 md:px-8 w-full overflow-hidden">
        
        {/* GRÁFICO */}
        <div className="w-full pt-6 overflow-hidden">
          <CardBarChart
            data={salesOverTimeQuery.data.data}
            currency={salesOverTimeQuery.data.currency}
            isLoading={salesOverTimeQuery.isFetching}
          />
        </div>

        {/* RANKINGS */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 pt-6">
          <CardTopClients
            clients={topClientsQuery.data.slice(0, topClientsLimit)}
            limit={topClientsLimit}
            onChangeLimit={setTopClientsLimit}
            currency={salesOverTimeQuery.data.currency}
          />

          <CardTopProducts
            products={topProductsQuery.data.slice(0, topProductsLimit)}
            limit={topProductsLimit}
            onChangeLimit={setTopProductsLimit}
            currency={salesOverTimeQuery.data.currency}
          />
        </div>
      </div>

    </AdminLayout>
  );
}